import React from "react";
import { Input } from "./input";

/**
 * Input numérique qui FORCE l'affichage en point décimal (même sur Windows fr-CH/fr-FR).
 *
 * Problème : `<input type="number">` natif applique la locale du navigateur/OS.
 *  - Windows fr-CH avec séparateur décimal "virgule" → l'input affiche "12,50" au lieu de "12.50"
 *  - Visuellement déroutant pour l'utilisateur (incohérence avec les autres affichages)
 *  - La valeur soumise reste correcte (point) mais l'UX est cassée.
 *
 * Solution : `type="text"` + `inputMode="decimal"` (clavier numérique sur mobile)
 * + nettoyage automatique de la virgule en point sur input.
 *
 * Props :
 *   - value          : string ou number (passé tel quel)
 *   - onChange       : fn(eventLike) — comme un input natif (e.target.value)
 *   - allowDecimal   : si false, refuse le point (entiers uniquement). Défaut true.
 *   - allowNegative  : défaut false
 *
 * Toutes les autres props sont passées à <Input> (className, placeholder, data-testid, etc.)
 */
export function NumericInput({
  value,
  onChange,
  allowDecimal = true,
  allowNegative = false,
  ...rest
}) {
  const handleChange = (e) => {
    let v = e.target.value;
    // Normalise : virgule → point pour la décimale
    if (allowDecimal) {
      v = v.replace(",", ".");
    } else {
      v = v.replace(/[.,]/g, "");
    }
    // Garde uniquement chiffres + (point si allowDecimal) + (- si allowNegative)
    const pattern = new RegExp(
      `[^0-9${allowDecimal ? "." : ""}${allowNegative ? "-" : ""}]`,
      "g"
    );
    v = v.replace(pattern, "");
    // 1 seul point max
    if (allowDecimal && (v.match(/\./g) || []).length > 1) {
      const idx = v.indexOf(".");
      v = v.slice(0, idx + 1) + v.slice(idx + 1).replace(/\./g, "");
    }
    // - uniquement en première position
    if (allowNegative) {
      v = v.replace(/-/g, (_, i) => (i === 0 ? "-" : ""));
    }
    // Propage au parent avec la chaîne nettoyée
    onChange({ ...e, target: { ...e.target, value: v } });
  };

  // Affiche la valeur en convertissant la virgule en point (par sécurité)
  const displayValue =
    value === null || value === undefined
      ? ""
      : typeof value === "number"
      ? String(value)
      : String(value).replace(",", ".");

  return (
    <Input
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      value={displayValue}
      onChange={handleChange}
      {...rest}
    />
  );
}

export default NumericInput;
